// DelSearchForAssociation.js
// In-place replacement component for searching and associating a DEL with a Safety Audit
// Uses shared table styles and FilterMenu pattern from DEL list tabs

import React, { useState, useRef, useEffect, useCallback } from "react";
import ConfirmationModal from "../../common/ConfirmationModal";
import { FilterButton } from "../../landingPage/components/buttons/DelActions";
import { DelFilterMenu } from "../../common/FilterMenu";
import tableStyles from "../../landingPage/components/del_table/table-common.module.css";
import styles from "./DelSearchForAssociation.module.css";

const DelSearchForAssociation = ({
  delSearchResults,
  delSearchTotalCount,
  delSearchCurrentPage,
  delSearchPageSize,
  delSearchSelectedDelId,
  delSearchLoading,
  delSearchFilters,
  delSearchFilterPhaseList,
  delSearchShowFilterMenu,
  safetyAuditFormActions,
  showLoading,
  hideLoading,
  showNotification,
  allProjectInfo,
  allInactiveProjects,
  allEmployees,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const filterMenuRef = useRef(null);

  // Fetch initial results on mount
  useEffect(() => {
    safetyAuditFormActions.fetchDelSearchResults(1, 10);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close filter menu on outside click
  useEffect(() => {
    if (!delSearchShowFilterMenu) return;
    const handleOutsideClick = (event) => {
      const isDatePickerClick =
        event.target.closest(".react-datepicker-popper") ||
        event.target.closest(".react-datepicker") ||
        event.target.closest("#date-picker-portal");

      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target) &&
        !isDatePickerClick
      ) {
        safetyAuditFormActions.closeDelSearchFilterMenu();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
    };
  }, [delSearchShowFilterMenu, safetyAuditFormActions]);

  const handleApplyFilters = useCallback(() => {
    safetyAuditFormActions.closeDelSearchFilterMenu();
    safetyAuditFormActions.fetchDelSearchResults(1, delSearchPageSize);
  }, [delSearchPageSize, safetyAuditFormActions]);

  const handleClearFilters = useCallback(() => {
    safetyAuditFormActions.clearDelSearchFilters();
    safetyAuditFormActions.closeDelSearchFilterMenu();
    safetyAuditFormActions.fetchDelSearchResults(1, delSearchPageSize);
  }, [delSearchPageSize, safetyAuditFormActions]);

  const handlePageChange = useCallback(
    (direction) => {
      const newPage =
        direction === "prev"
          ? delSearchCurrentPage - 1
          : delSearchCurrentPage + 1;
      safetyAuditFormActions.fetchDelSearchResults(newPage, delSearchPageSize);
    },
    [delSearchCurrentPage, delSearchPageSize, safetyAuditFormActions]
  );

  const handlePageSizeChange = useCallback(
    (e) => {
      const newSize = parseInt(e.target.value, 10);
      safetyAuditFormActions.fetchDelSearchResults(1, newSize);
    },
    [safetyAuditFormActions]
  );

  const handleRowClick = useCallback(
    (del) => {
      safetyAuditFormActions.selectDelSearchResult(del.id);
    },
    [safetyAuditFormActions]
  );

  const handleRowDoubleClick = useCallback(
    (del) => {
      safetyAuditFormActions.selectDelSearchResult(del.id);
      setShowConfirmation(true);
    },
    [safetyAuditFormActions]
  );

  const handleSelectDel = () => {
    if (!delSearchSelectedDelId) return;
    setShowConfirmation(true);
  };

  const handleConfirmAssociation = () => {
    setShowConfirmation(false);
    safetyAuditFormActions.handleAssociateDel(
      showLoading,
      hideLoading,
      showNotification
    );
  };

  // Helper to display status text
  const getStatusLabel = (del) => {
    if (del.approved === "True") return "Approved";
    if (del.submit === "True") return "Submitted";
    return "Draft";
  };

  // Helper to determine if DEL has a JSA
  const hasJsa = (del) => {
    return del.jsa_not_required === 0 && del.jsa_already_completed === 0;
  };

  return (
    <div className={styles.delSearchContainer}>
      {/* Header */}
      <div className={styles.delSearchHeader}>
        <h2 className={styles.headerTitle}>Find DEL to Associate</h2>
        <div className={styles.headerButtons}>
          <div
            style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}
            ref={filterMenuRef}
          >
            <FilterButton
              filterFunction={() => safetyAuditFormActions.toggleDelSearchFilterMenu()}
            />
            {delSearchShowFilterMenu && (
              <DelFilterMenu
                delTab="submitted"
                filters={delSearchFilters}
                delStatusOptions={[
                  { value: "", label: "All" },
                  { value: "0", label: "Draft" },
                  { value: "1", label: "Submitted" },
                  { value: "2", label: "Approved" },
                ]}
                filterPhaseList={delSearchFilterPhaseList}
                filterPhaseActionFunction={(list) =>
                  safetyAuditFormActions.setDelSearchFilterPhaseList(list)
                }
                updateFilterAction={(filter) =>
                  safetyAuditFormActions.updateDelSearchFilter(filter)
                }
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                allProjectInfo={allProjectInfo}
                allInactiveProjects={allInactiveProjects}
                allEmployees={allEmployees}
              />
            )}
          </div>
          <button
            className={styles.cancelButton}
            onClick={() => safetyAuditFormActions.cancelDelSearch()}
          >
            Cancel
          </button>
          <button
            className={styles.selectButton}
            onClick={handleSelectDel}
            disabled={!delSearchSelectedDelId}
          >
            Select DEL
          </button>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className={styles.paginationControls}>
        <div className={styles.paginationLeftControls}>
          <button
            onClick={() => handlePageChange("prev")}
            disabled={delSearchCurrentPage <= 1}
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange("next")}
            disabled={delSearchCurrentPage * delSearchPageSize >= delSearchTotalCount}
          >
            Next
          </button>
          <div className={styles.pageSizeSelector}>
            <label htmlFor="delSearchPageSize">Rows:</label>
            <select
              id="delSearchPageSize"
              className={styles.pageSizeSelect}
              value={delSearchPageSize}
              onChange={handlePageSizeChange}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
        <span>
          Showing {Math.min((delSearchCurrentPage - 1) * delSearchPageSize + 1, delSearchTotalCount)} -{" "}
          {Math.min(delSearchCurrentPage * delSearchPageSize, delSearchTotalCount)} of {delSearchTotalCount}{" "}
          DELs
        </span>
      </div>

      {/* Loading State */}
      {delSearchLoading ? (
        <div className={styles.loadingContainer}>Loading DELs...</div>
      ) : delSearchResults.length === 0 ? (
        <div className={styles.emptyContainer}>
          No DELs found. Try adjusting your filters.
        </div>
      ) : (
        <div className={tableStyles.tableWrapper}>
          <div className={tableStyles.delTableContainer}>
            {/* Card View (Mobile & Tablet) */}
            <div className={tableStyles.cardContainer}>
              <div className={tableStyles.cardList}>
                {delSearchResults.map((del) => (
                  <div
                    key={`card-${del.id}`}
                    onClick={() => handleRowClick(del)}
                    onDoubleClick={() => handleRowDoubleClick(del)}
                    className={`${tableStyles.card} ${
                      del.id === delSearchSelectedDelId
                        ? tableStyles.selectedCard
                        : ""
                    }`}
                  >
                    <div className={tableStyles.cardHeaderRow}>
                      <span className={tableStyles.statusLabel}>
                        {del.del_date || "-"}
                      </span>
                      <span className={tableStyles.statusLabel}>
                        {getStatusLabel(del)}
                      </span>
                    </div>
                    <div className={tableStyles.cardContent}>
                      <div className={tableStyles.dataRow}>
                        <span className={tableStyles.label}>Project #:</span>
                        <span className={tableStyles.value}>
                          {del.project_number !== "-"
                            ? del.project_number
                            : del.project_name_description || "-"}
                        </span>
                      </div>
                      <div className={tableStyles.dataRow}>
                        <span className={tableStyles.label}>Phase:</span>
                        <span className={tableStyles.value}>
                          {del.phase_id || "-"}
                        </span>
                      </div>
                      <div className={tableStyles.dataRow}>
                        <span className={tableStyles.label}>JSA:</span>
                        <span className={tableStyles.value}>
                          {hasJsa(del) ? "Yes" : "No"}
                        </span>
                      </div>
                      <div className={tableStyles.dataRow}>
                        <span className={tableStyles.label}>Created:</span>
                        <span className={tableStyles.value}>
                          {del.del_creation_date || "-"}
                        </span>
                      </div>
                      <div className={tableStyles.dataRow}>
                        <span className={tableStyles.label}>Submitted By:</span>
                        <span className={tableStyles.value}>
                          {del.submitted_by_name || del.user_full_name || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Table View (Desktop) */}
            <table
              className={`${tableStyles.table} ${tableStyles.reportingTable}`}
            >
              <thead>
                <tr>
                  <th>DEL Date</th>
                  <th>Created Date</th>
                  <th>Updated Date</th>
                  <th>Project #</th>
                  <th>Phase ID</th>
                  <th>Task ID</th>
                  <th>JSA</th>
                  <th>Submitted By</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {delSearchResults.map((del) => (
                  <tr
                    key={`row-${del.id}`}
                    onClick={() => handleRowClick(del)}
                    onDoubleClick={() => handleRowDoubleClick(del)}
                    className={
                      del.id === delSearchSelectedDelId
                        ? tableStyles.selectedRow
                        : ""
                    }
                  >
                    <td title={del.del_date}>{del.del_date || "-"}</td>
                    <td title={del.del_creation_date}>
                      {del.del_creation_date || "-"}
                    </td>
                    <td title={del.updated}>{del.updated || "-"}</td>
                    <td
                      title={
                        del.project_number !== "-"
                          ? del.project_number
                          : del.project_name_description
                      }
                    >
                      {del.project_number !== "-"
                        ? del.project_number
                        : del.project_name_description || "-"}
                    </td>
                    <td title={del.phase_id}>{del.phase_id || "-"}</td>
                    <td title={del.task_id}>{del.task_id || "-"}</td>
                    <td title={hasJsa(del) ? "Yes" : "No"}>
                      {hasJsa(del) ? "Yes" : "No"}
                    </td>
                    <td title={del.submitted_by_name || del.user_full_name}>
                      {del.submitted_by_name || del.user_full_name || "-"}
                    </td>
                    <td title={getStatusLabel(del)}>{getStatusLabel(del)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmation}
        title="Associate DEL"
        message="Associating this DEL will update the project information and add any new employees from the DEL to this safety audit. Continue?"
        onConfirm={handleConfirmAssociation}
        onCancel={() => setShowConfirmation(false)}
        confirmText="Associate"
        cancelText="Cancel"
      />
    </div>
  );
};

export default DelSearchForAssociation;